// src/pages/LibrariesPage.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { LibrariesTable } from '../tables/PageTables.jsx';
import { getLibraries, getRooms, getAllStudentsData } from '../services/api.js';
import axios from '../api/api1';
import { DEMO_LOGIN_ENABLED } from '../demoMode.js';
import { useToast } from '../context/ToastContext.jsx';
import { FaBook, FaPlus } from 'react-icons/fa';
import { FiRefreshCw } from 'react-icons/fi';
import UiSelect from '../components/UiSelect.jsx';
import { useUrlFilters } from '../hooks/useUrlFilters.js';
import '../styles/pages/libraries.css';

const emptyLibrary = { library_name: '', location: '', description: '', status: 'active' };
const emptyRoom = { room_name: '', room_number: '', total_seat_count: '', floor: '1', ac_seats: '', non_ac_seats: '' };

export function LibrariesPage({ toast, setPage, setSelectedLibrary }) {
  const [libraries, setLibraries] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [editing, setEditing] = useState(null);
  const [editingMode, setEditingMode] = useState('details');
  const [form, setForm] = useState(emptyLibrary);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [managing, setManaging] = useState(null);
  const [manageTab, setManageTab] = useState('list');
  const [roomForm, setRoomForm] = useState(emptyRoom);
  const [savingRoom, setSavingRoom] = useState(false);
  const [roomBusyId, setRoomBusyId] = useState(null);
  const [unassignedRoomId, setUnassignedRoomId] = useState('');
  const [deletingLibraryId, setDeletingLibraryId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState('');
  const { showToast } = useToast();

  useUrlFilters([
    { key: 'search', value: query, setValue: setQuery, defaultValue: '' },
    { key: 'status', value: statusFilter, setValue: setStatusFilter, defaultValue: 'All' },
  ]);

  const notify = (message) => { if (toast) toast(message); else if (showToast) showToast(message, 'success'); };

  useEffect(() => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userinfo') || '{}');
      setCurrentUserRole(String(userInfo.role || '').toLowerCase().replace(/[\s_-]/g, ''));
    } catch {
      setCurrentUserRole('');
    }
  }, []);

  // Only the tenant owner may create, rename or delete libraries.
  const canManageLibraries = currentUserRole === 'tenantowner';

  // Jump to a page, optionally scoping the app to a specific library first.
  const goToPage = (page, library) => {
    if (library && setSelectedLibrary) {
      const libraryScope = library.library_id ?? library.id ?? library.library_name ?? library.name;
      setSelectedLibrary(String(libraryScope));
    }
    setPage?.(page);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      if (DEMO_LOGIN_ENABLED) {
        const previewLibraries = [
          { id: 'PREVIEW-LIB-1', library_id: 'PREVIEW-LIB-1', name: 'Central Study Library', library_name: 'Central Study Library', location: 'Pune, Maharashtra', description: 'Main study branch', status: 'Active', rooms: 2, seats: 80, students: 54, occupancy: 68, created: '15 Jan 2024' },
          { id: 'PREVIEW-LIB-2', library_id: 'PREVIEW-LIB-2', name: 'North Campus Library', library_name: 'North Campus Library', location: 'Pune, Maharashtra', description: 'North campus branch', status: 'Active', rooms: 1, seats: 40, students: 29, occupancy: 73, created: '03 Mar 2024' }
        ];
        const previewRooms = [
          { id: 'PREVIEW-R-1', library_id: 'PREVIEW-LIB-1', room_name: 'Quiet Hall', room_number: '101', total_seat_count: 40, floor: '1' },
          { id: 'PREVIEW-R-2', library_id: 'PREVIEW-LIB-1', room_name: 'Reading Room', room_number: '102', total_seat_count: 40, floor: '2' },
          { id: 'PREVIEW-R-3', library_id: 'PREVIEW-LIB-2', room_name: 'Main Hall', room_number: '201', total_seat_count: 40, floor: '1' }
        ];
        let savedPreview;
        try { savedPreview = JSON.parse(localStorage.getItem('srms-preview-libraries') || 'null'); } catch { savedPreview = null; }
        setLibraries(savedPreview?.libraries || previewLibraries);
        setRooms(savedPreview?.rooms || previewRooms);
        setStudents(Array.from({ length: 83 }, (_, i) => ({ student_id: `PREVIEW-ST-${i + 1}`, library_id: i < 54 ? 'PREVIEW-LIB-1' : 'PREVIEW-LIB-2' })));
        return;
      }
      const [libsRes, roomsRes, studentsRes] = await Promise.all([
        getLibraries(), getRooms(), getAllStudentsData(null, null, true)
      ]);
      const roomsData = roomsRes || [];
      const studentsData = studentsRes.students || studentsRes || [];
      setRooms(roomsData);
      setStudents(studentsData);

      const mapped = (libsRes || []).map((l, idx) => {
        const libId = l.library_id || l.id || `LIB-${idx}`;
        const libRooms = roomsData.filter(r => String(r.library_id) === String(libId));
        const libStudents = studentsData.filter(s => String(s.library_id) === String(libId) || String(s.libraryId) === String(libId));
        const totalSeats = libRooms.reduce((a, r) => a + Number(r.total_seat_count || r.total_seats || 0), 0);
        return {
          id: libId, library_id: libId,
          name: l.library_name || l.name,
          library_name: l.library_name || l.name,
          location: l.location || l.address || `${l.library_name || l.name}, Maharashtra`,
          description: l.description || '', status: l.status || 'Active',
          rooms: libRooms.length, seats: totalSeats, students: libStudents.length,
          occupancy: totalSeats ? Math.round((libStudents.length / totalSeats) * 100) : 0,
          created: l.created_at ? new Date(l.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A',
          _raw: l
        };
      });
      setLibraries(mapped);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => {
    if (DEMO_LOGIN_ENABLED && !loading) {
      localStorage.setItem('srms-preview-libraries', JSON.stringify({ libraries, rooms }));
    }
  }, [libraries, rooms, loading]);
  const handleRefresh = async () => { setRefreshing(true); await fetchAll(); };
  const onManage = (lib) => {
    setManaging(lib);
    setManageTab('list');
    setUnassignedRoomId('');
    setRoomForm({ ...emptyRoom, library_id: lib.library_id || lib.id });
  };
  const managingRooms = useMemo(() => rooms.filter(r => String(r.library_id) === String(managing?.library_id || managing?.id)), [rooms, managing]);
  const unassignedRooms = useMemo(() => rooms.filter(r => !r.library_id), [rooms]);

  const stats = useMemo(() => ({
    totalLibraries: libraries.length,
    activeLibraries: libraries.filter(l => (l.status || '').toLowerCase() === 'active').length,
    totalStudents: students.length,
    totalSeats: rooms.reduce((a, r) => a + Number(r.total_seat_count || 0), 0)
  }), [libraries, rooms, students]);

  const visibleLibraries = useMemo(() => libraries.filter(lib => {
    const matches = `${lib.name} ${lib.location}`.toLowerCase().includes(query.toLowerCase());
    return matches && (statusFilter === 'All' || lib.status.toLowerCase() === statusFilter.toLowerCase());
  }), [libraries, query, statusFilter]);

  const openEditor = (lib = null, mode = 'details') => {
    const libId = lib?.library_id ?? lib?.id ?? null;
    setEditing(libId ?? 'new');
    setEditingMode(mode);
    setForm(lib ? { library_name: lib.library_name || lib.name || '', location: lib.location || '', description: lib.description || '', status: String(lib.status || 'active').toLowerCase() } : emptyLibrary);
  };

  const saveLibrary = async (e) => {
    e.preventDefault();
    if (!canManageLibraries) {
      if (showToast) showToast('Only the tenant owner can manage libraries.', 'error');
      return;
    }
    const payload = {
      library_name: form.library_name,
      location: form.location,
      description: form.description,
      status: form.status
    };
    if (DEMO_LOGIN_ENABLED) {
      const id = editing === 'new' ? `PREVIEW-LIB-${Date.now()}` : editing;
      setLibraries(current => editing === 'new'
        ? [...current, { id, library_id: id, name: form.library_name, library_name: form.library_name, location: form.location, description: form.description, status: form.status, rooms: 0, seats: 0, students: 0, occupancy: 0, created: 'Today' }]
        : current.map(lib => (String(lib.library_id || lib.id)) === String(editing) ? { ...lib, name: form.library_name, library_name: form.library_name, location: editingMode === 'rename' ? lib.location : form.location, description: editingMode === 'rename' ? lib.description : form.description, status: editingMode === 'rename' ? lib.status : form.status } : lib));
      setEditing(null); setForm(emptyLibrary);
      notify(editing === 'new' ? 'Library created' : 'Library updated');
      return;
    }
    try {
      if (editing === 'new') await axios.post('/api/libraries/create-library', payload);
      else await axios.put(`/api/libraries/${editing}`, payload);
      setEditing(null); setForm(emptyLibrary); await fetchAll();
      notify(editing === 'new' ? 'Library created' : 'Library updated');
    } catch (error) {
      console.error('Failed to save library:', error);
      const msg = error.response?.data?.error || error.response?.data?.message || 'Could not save library changes. Please try again.';
      if (showToast) showToast(msg, 'error'); else window.alert(msg);
    }
  };

  const deleteLibrary = async (lib) => {
    if (!canManageLibraries) {
      if (showToast) showToast('Only the tenant owner can manage libraries.', 'error');
      return;
    }
    const id = lib.library_id || lib.id;
    if (!window.confirm(`Delete ${lib.name || lib.library_name}? This removes the library and all associated data.`)) return;
    setDeletingLibraryId(id);
    if (DEMO_LOGIN_ENABLED) {
      setLibraries(current => current.filter(item => String(item.library_id || item.id) !== String(id)));
      setRooms(current => current.filter(room => String(room.library_id) !== String(id)));
      if (String(managing?.library_id || managing?.id) === String(id)) setManaging(null);
      setDeletingLibraryId(null);
      if (showToast) showToast('Library deleted successfully', 'success'); else notify('Library deleted');
      return;
    }
    try {
      await axios.delete(`/api/libraries/${id}`);
      if (String(managing?.library_id || managing?.id) === String(id)) setManaging(null);
      await fetchAll();
      if (showToast) showToast('Library deleted successfully', 'success'); else notify('Library deleted');
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Could not delete library.';
      if (showToast) showToast(msg, 'error'); else window.alert(msg);
    } finally {
      setDeletingLibraryId(null);
    }
  };

  const saveRoom = async (e) => {
    e.preventDefault();
    const acSeats = Number(roomForm.ac_seats) || 0;
    const nonAcSeats = Number(roomForm.non_ac_seats) || 0;
    const capacity = acSeats + nonAcSeats || Number(roomForm.total_seat_count) || 0;
    if (!capacity) { window.alert('Add at least one AC or Non-AC seat.'); return; }
    const libraryId = managing.library_id || managing.id;
    setSavingRoom(true);
    try {
      if (DEMO_LOGIN_ENABLED) {
        const roomId = `PREVIEW-R-${Date.now()}`;
        const newRoom = {
          ...roomForm, id: roomId, room_id: roomId,
          library_id: libraryId, total_seat_count: capacity, ac_seats: acSeats, non_ac_seats: nonAcSeats
        };
        setRooms(current => [...current, newRoom]);
        setLibraries(current => current.map(lib => String(lib.library_id || lib.id) === String(libraryId) ? { ...lib, rooms: (lib.rooms || 0) + 1, seats: (lib.seats || 0) + capacity } : lib));
        setManaging(current => current ? { ...current, rooms: (current.rooms || 0) + 1, seats: (current.seats || 0) + capacity } : current);
        setRoomForm({ ...emptyRoom, library_id: libraryId });
        notify('Room created');
        return;
      }
      await axios.post('/api/rooms/create-with-seats', {
        roomname: roomForm.room_name,
        floorno: Number(roomForm.floor) || 0,
        acseats: acSeats,
        non_acseats: nonAcSeats,
        library_id: libraryId,
        status: 'active'
      });
      setRoomForm({ ...emptyRoom, library_id: libraryId });
      await fetchAll();
      notify('Room created');
    } catch (error) {
      window.alert(error.response?.data?.error || error.response?.data?.message || 'Could not create room.');
    } finally { setSavingRoom(false); }
  };

  const assignRoom = async () => {
    if (!unassignedRoomId || !managing) return;
    const libraryId = managing.library_id || managing.id;
    setSavingRoom(true);
    try {
      if (DEMO_LOGIN_ENABLED) {
        setRooms(current => current.map(room => String(room.room_id || room.id) === String(unassignedRoomId) ? { ...room, library_id: libraryId } : room));
      } else {
        await axios.put(`/api/rooms/${unassignedRoomId}`, { library_id: libraryId });
        await fetchAll();
      }
      setUnassignedRoomId('');
      notify('Room assigned');
    } catch (error) {
      window.alert(error.response?.data?.error || 'Could not assign room.');
    } finally { setSavingRoom(false); }
  };

  const unassignRoom = async (room) => {
    const roomId = room.room_id || room.id;
    if (!window.confirm(`Remove ${room.room_name} from this library?`)) return;
    setRoomBusyId(roomId);
    try {
      if (DEMO_LOGIN_ENABLED) {
        setRooms(current => current.map(item => String(item.room_id || item.id) === String(roomId) ? { ...item, library_id: null } : item));
      } else {
        await axios.put(`/api/rooms/${roomId}`, { library_id: null });
        await fetchAll();
      }
      notify('Room removed from library');
    } catch (error) {
      window.alert(error.response?.data?.error || 'Could not remove room.');
    } finally { setRoomBusyId(null); }
  };

  const toggleStatus = async (id, shouldActivate) => {
    if (!canManageLibraries) {
      if (showToast) showToast('Only the tenant owner can manage libraries.', 'error');
      return;
    }
    const newStatus = shouldActivate ? 'Active' : 'Inactive';
    if (DEMO_LOGIN_ENABLED) {
      setLibraries(cur => cur.map(i => String(i.library_id || i.id) === String(id) ? { ...i, status: newStatus } : i));
      return;
    }
    try { await axios.put(`/api/libraries/${id}`, { status: newStatus.toLowerCase() }); fetchAll(); }
    catch { setLibraries(cur => cur.map(i => String(i.library_id || i.id) === String(id) ? { ...i, status: newStatus } : i)); }
  };

  if (loading) {
    return (
      <div className="libraries-page">
        <header className="dash-header libraries-page-header">
          <div className="dash-header-bar">
            <div className="libraries-header-left">
              <div className="libraries-header-icon" aria-hidden="true"><FaBook /></div>
              <div className="dash-header-info libraries-header-info">
                <div className="libraries-heading-copy">
                  <h1 className="dash-header-title">Library Management</h1>
                  <p className="dash-header-subtitle">Manage library branches, rooms, and seat capacity.</p>
                </div>
                <div className="dash-header-actions">
                  <button type="button" className="dash-action-btn" disabled aria-label="Refresh libraries">
                    <span className="dash-action-icon"><FiRefreshCw aria-hidden="true" /></span>
                    <span>Refresh</span>
                  </button>
                  {canManageLibraries && <button type="button" className="dash-action-btn primary" disabled aria-label="Add library">
                    <FaPlus aria-hidden="true" />
                    <span>Add Library</span>
                  </button>}
                </div>
              </div>
            </div>
          </div>
        </header>
        <div className="stats-grid four figma-stats-grid">
          {[1,2,3,4].map((i)=>(<div key={i} className="figma-stat-card skeleton-card"><div className="figma-stat-top"><div className="skeleton-text short" /><div className="skeleton-pill" /></div><div className="skeleton-text large" /></div>))}
        </div>
        <section className="panel libraries-panel figma-table-card" aria-busy="true">
          <div className="libraries-toolbar">
            <div className="libraries-filters">
              <div className="skeleton-text" style={{ maxWidth: 440, height: 36 }} />
              <div className="skeleton-text" style={{ width: 120, height: 36 }} />
              <div className="skeleton-text short" />
            </div>
          </div>
          <div className="table-scroll libraries-table-scroll libraries-skel-scroll" aria-hidden="true">
            <table className="libraries-data-table libraries-skel-table">
              <colgroup>
                <col style={{ width: '7%' }} />
                <col style={{ width: '31%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '15%' }} />
              </colgroup>
              <thead>
                <tr>
                  {['Sr No', 'Library Name', 'Created At', 'Rooms', 'Total Seats', 'Actions'].map(label => (
                    <th key={label}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, row) => (
                  <tr key={row}>
                    <td><span className="dash-skel-line short" /></td>
                    <td>
                      <div className="libraries-skel-name">
                        <span className="dash-skel-icon small" />
                        <span className="dash-skel-line wide" />
                      </div>
                    </td>
                    <td><span className="dash-skel-line medium" /></td>
                    <td><span className="dash-skel-line short" /></td>
                    <td><span className="dash-skel-line short" /></td>
                    <td>
                      <div className="libraries-skel-actions">
                        <span className="dash-skel-icon small" />
                        <span className="dash-skel-icon small" />
                        <span className="dash-skel-icon small" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="libraries-page">
      <header className="dash-header libraries-page-header">
        <div className="dash-header-bar">
          <div className="libraries-header-left">
            <div className="libraries-header-icon" aria-hidden="true"><FaBook /></div>
            <div className="dash-header-info libraries-header-info">
              <div className="libraries-heading-copy">
                <h1 className="dash-header-title">Library Management</h1>
                <p className="dash-header-subtitle">Manage library branches, rooms, and seat capacity.</p>
              </div>
              <div className="dash-header-actions">
                <button type="button" className="dash-action-btn" onClick={handleRefresh} disabled={refreshing} aria-label="Refresh libraries">
                  <span className={`dash-action-icon ${refreshing ? 'is-loading' : ''}`}><FiRefreshCw aria-hidden="true" /></span>
                  <span>{refreshing ? 'Refreshing' : 'Refresh'}</span>
                </button>
                {canManageLibraries && (
                  <button type="button" className="dash-action-btn primary" onClick={() => openEditor()} aria-label="Add library">
                    <FaPlus aria-hidden="true" />
                    <span>Add Library</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="stats-grid four figma-stats-grid">
        <div className="figma-stat-card"><div className="figma-stat-top"><span>Total Libraries</span><div className="figma-icon light-violet"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></div></div><h2>{stats.totalLibraries}</h2></div>
        <div className="figma-stat-card"><div className="figma-stat-top"><span>Active Libraries</span><div className="figma-icon light-green"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></div></div><h2>{stats.activeLibraries}</h2></div>
        <button type="button" className="figma-stat-card figma-stat-link" onClick={() => goToPage('Students')} aria-label="View all students"><div className="figma-stat-top"><span>Total Students</span><div className="figma-icon light-purple"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></div></div><h2>{stats.totalStudents}</h2></button>
        <button type="button" className="figma-stat-card figma-stat-link" onClick={() => goToPage('Seats')} aria-label="View all seats"><div className="figma-stat-top"><span>Total Seats</span><div className="figma-icon light-yellow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="4" y="6" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M6 16v2M18 16v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg></div></div><h2>{stats.totalSeats}</h2></button>
      </div>

      <section className="panel libraries-panel figma-table-card">
        <div className="libraries-toolbar">
          <div className="libraries-filters">
            <label className="search-wrap"><span>⌕</span><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search libraries..." /></label>
            <div style={{ width: 140 }}>
              <UiSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </UiSelect>
            </div>
            <span className="libraries-result-count">{visibleLibraries.length} of {libraries.length} locations</span>
          </div>
        </div>
        <LibrariesTable libraries={visibleLibraries} onRename={(lib) => openEditor(lib, 'rename')} onToggleStatus={toggleStatus} onManage={onManage} onDelete={deleteLibrary} deletingLibraryId={deletingLibraryId} canManage={canManageLibraries} />
      </section>

      {editing !== null && (
        <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setEditing(null)}>
          <section className="modal library-editor">
            <div className="modal-head"><div><span className="eyebrow">LIBRARIES</span><h2>{editing === 'new' ? 'Add Library' : editingMode === 'rename' ? 'Rename Library' : 'Edit Library'}</h2></div><button className="close-button" onClick={() => setEditing(null)}>×</button></div>
            <form onSubmit={saveLibrary}>
              <div className="form-grid">
                <label>Library Name <em>*</em><input required autoFocus value={form.library_name} onChange={(e) => setForm({ ...form, library_name: e.target.value })} placeholder="Enter library name" /></label>
                {editingMode !== 'rename' && <>
                  <label>Location <em>*</em><input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Enter location" /></label>
                  <label style={{ gridColumn: '1 / -1' }}>Description <em>*</em><input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Enter description" /></label>
                  <label>Status <em>*</em><UiSelect value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="active">Active</option><option value="inactive">Inactive</option></UiSelect></label>
                </>}
              </div>
              <div className="modal-actions"><button type="button" className="button ghost" onClick={() => setEditing(null)}>Cancel</button><button type="submit" className="button primary">Save</button></div>
            </form>
          </section>
        </div>
      )}

      {managing && (
        <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setManaging(null)}>
          <section className="modal library-editor manage-modal">
            <div className="modal-head"><div><span className="eyebrow">MANAGE LIBRARY</span><h2>{managing.name || managing.library_name}</h2><p className="muted-note">{managing.location || '-'} • Rooms: {managingRooms.length} • Seats: {managingRooms.reduce((total, room) => total + Number(room.total_seat_count || room.total_seats || 0), 0)} • Students: {managing.students ?? 0}</p></div><button className="close-button" aria-label="Close manage library dialog" onClick={() => setManaging(null)}>×</button></div>
            <div className="library-manage-actions"><button type="button" className="button ghost small" onClick={() => { setManaging(null); goToPage('Students', managing); }}>Students</button><button type="button" className="button ghost small" onClick={() => { setManaging(null); goToPage('Seats', managing); }}>Seats</button><button type="button" className="button ghost small" onClick={() => { const library = libraries.find(item => String(item.library_id || item.id) === String(managing.library_id || managing.id)); if (library) { setManaging(null); openEditor(library, 'rename'); } }}>Rename</button><button type="button" className="button danger-ghost small" onClick={() => deleteLibrary(managing)}>Delete Library</button></div>
            <div className="manage-tabs" aria-label="Library room management">
              <button type="button" aria-pressed={manageTab === 'list'} className={`manage-tab ${manageTab === 'list' ? 'active' : ''}`} onClick={() => setManageTab('list')}>Room List <span>{managingRooms.length}</span></button>
              <button type="button" aria-pressed={manageTab === 'create'} className={`manage-tab ${manageTab === 'create' ? 'active' : ''}`} onClick={() => setManageTab('create')}>Create New Room</button>
              <button type="button" aria-pressed={manageTab === 'assign'} className={`manage-tab ${manageTab === 'assign' ? 'active' : ''}`} onClick={() => setManageTab('assign')}>Assign Existing Room <span>{unassignedRooms.length}</span></button>
            </div>
            <div className="manage-body">
              {manageTab === 'list' && <div className="manage-section">
                <div className="manage-section-head"><h4>Rooms in this Library</h4></div>
                <div className="table-scroll rooms-table-wrap">
                  <table><thead><tr><th>Room Name</th><th>Room No</th><th>Floor</th><th>Seats</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>{managingRooms.length === 0 ? <tr><td colSpan={6} className="empty-cell">No rooms in this library yet. Create a room or assign an existing one.</td></tr> : managingRooms.map(r => {
                      const roomId = r.room_id || r.id;
                      return <tr key={roomId}><td><div className="lib-name-wrap"><div className="icon room-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M4 20V5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5V20M2 20h20M8 8h2m4 0h2m-8 4h2m4 0h2m-7 8v-4h4v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg></div><b>{r.room_name || r.roomname || 'Unnamed room'}</b></div></td><td>{r.room_number || '-'}</td><td>{r.floor ?? r.floor_no ?? '-'}</td><td>{r.total_seat_count || r.total_seats || 0}</td><td><span className={`status ${String(r.status || 'active').toLowerCase() === 'active' ? 'good' : 'muted'}`}>{r.status || 'Active'}</span></td><td><button type="button" className="button ghost small" disabled={String(roomBusyId) === String(roomId)} onClick={() => unassignRoom(r)}>{String(roomBusyId) === String(roomId) ? 'Removing...' : 'Remove'}</button></td></tr>;
                    })}</tbody>
                  </table>
                </div>
              </div>}
              {manageTab === 'assign' && <div className="manage-section">
                  <div className="manage-section-head"><h4>Assign Existing Room</h4></div>
                  <div className="assign-room-bar">
                    <div>
                      <label>Select Unassigned Room</label>
                      <UiSelect value={unassignedRoomId} onChange={(e) => setUnassignedRoomId(e.target.value)}>
                        <option value="">Select a room</option>
                        {unassignedRooms.map(r => <option key={r.room_id || r.id} value={r.room_id || r.id}>{r.room_name || r.roomname} (Room {r.room_number || '-'} | Floor {r.floor ?? r.floor_no ?? 0} | Seats: {r.total_seat_count || r.total_seats || 0})</option>)}
                      </UiSelect>
                    </div>
                    <button type="button" className="button primary" disabled={!unassignedRoomId || savingRoom || !unassignedRooms.length} onClick={assignRoom}>{savingRoom ? 'Assigning...' : 'Assign Room'}</button>
                  </div>
                  {!unassignedRooms.length && <p className="manage-empty-note">There are no unassigned rooms available.</p>}
              </div>}
              {manageTab === 'create' && <div className="manage-section">
                <div className="manage-section-head"><h4>Create New Room</h4></div>
                <form onSubmit={saveRoom} className="add-room-form">
                  <div className="form-grid">
                    <label>Room Name <em>*</em><input required value={roomForm.room_name} onChange={e => setRoomForm({ ...roomForm, room_name: e.target.value })} placeholder="Hall A" /></label>
                    <label>Room Number<input value={roomForm.room_number} onChange={e => setRoomForm({ ...roomForm, room_number: e.target.value })} placeholder="101" /></label>
                    <label>AC Seats<input type="number" min="0" value={roomForm.ac_seats} onChange={e => setRoomForm({ ...roomForm, ac_seats: e.target.value })} placeholder="10" /></label>
                    <label>Non-AC Seats<input type="number" min="0" value={roomForm.non_ac_seats} onChange={e => setRoomForm({ ...roomForm, non_ac_seats: e.target.value })} placeholder="10" /></label>
                    <label>Floor<input type="number" min="0" value={roomForm.floor} onChange={e => setRoomForm({ ...roomForm, floor: e.target.value })} placeholder="1" /></label>
                  </div>
                  <div className="modal-actions no-border"><button type="submit" className="button primary" disabled={savingRoom}>{savingRoom ? 'Creating...' : 'Create & Add Room'}</button></div>
                </form>
              </div>}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
